import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const MIN_PASSWORD_LENGTH = 8;

export default function ResetPassword() {
  const [token, setToken]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // The token arrives as ?rt= in the query string. It began life in the URL
  // fragment, to keep it out of server logs — but the in-app browsers that
  // mail clients open links in routinely drop fragments, which left the page
  // loading with no token and no way to recover. The fragment forms are still
  // read so links already sitting in an inbox keep working.
  useEffect(() => {
    const fromQuery = new URLSearchParams(window.location.search).get('rt');
    if (fromQuery) {
      setToken(fromQuery);
      return;
    }
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const found = params.get('reset_token') || params.get('token');
    if (found) {
      setToken(found);
      // Keep it out of the address bar and browser history. Safe to clear
      // here, unlike the query form, because a refresh of a fragment URL
      // would have lost it anyway.
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password !== confirm) {
      setError('Those passwords don’t match');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/reset-password', { token, password });
      if (data.token) {
        // Resetting signs you straight in — the response carries a fresh
        // token, since every one issued earlier is now rejected.
        login(data.token, data.account);
        navigate('/dashboard');
      } else {
        navigate('/login');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const marketingUrl = process.env.REACT_APP_MARKETING_URL || 'https://velaleads.ca';

  return (
    <div style={pageStyle}>
      <a href={marketingUrl} style={logoStyle} aria-label="Vela home">
        <img src="/logos/vela-wordmark.png" alt="" style={{ height: 22, width: 'auto', display: 'block' }} />
      </a>

      <div style={cardStyle}>
        <h1 style={headingStyle}>Choose a new password</h1>

        {!token ? (
          <>
            <p style={subheadStyle}>
              This link didn&rsquo;t come through complete. Ask for a new one and it should work.
            </p>
            <p style={switchModeStyle}>
              <span onClick={() => navigate('/login')} style={switchModeLinkStyle}>
                Back to sign in
              </span>
            </p>
          </>
        ) : (
          <>
            <p style={subheadStyle}>
              Once you save it, you&rsquo;ll be signed out everywhere else.
            </p>

            {error && <div style={errorStyle}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <label style={labelStyle}>New password</label>
              <input style={inputStyle} type="password" required autoFocus
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters" />

              <label style={labelStyle}>Confirm new password</label>
              <input style={inputStyle} type="password" required
                value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="••••••••" />

              <button type="submit" disabled={loading}
                style={{ ...buttonStyle, opacity: loading ? 0.7 : 1 }}>
                <span style={buttonTextStyle}>
                  {loading ? 'Please wait…' : 'Save new password'}
                </span>
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
  justifyContent: 'center', gap: 28, background: 'var(--vela-bg)', padding: '40px 20px',
};
const logoStyle = { display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' };
const cardStyle = {
  background: 'var(--vela-glass-bg)', backdropFilter: 'blur(24px) saturate(160%)',
  WebkitBackdropFilter: 'blur(24px) saturate(160%)', border: '1px solid var(--vela-glass-border)',
  borderRadius: 20, padding: '40px 36px', width: 380, maxWidth: '100%',
};
const headingStyle = {
  fontFamily: "'Inter', -apple-system, sans-serif", fontWeight: 600, fontSize: 22,
  color: 'var(--vela-text)', marginBottom: 6, lineHeight: 1.15, textAlign: 'center',
};
const subheadStyle = { color: 'var(--vela-text-mid)', marginBottom: 28, fontSize: 14, textAlign: 'center' };
const errorStyle = {
  background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)',
  color: '#fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13,
};
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 5, color: 'var(--vela-text-mid)' };
const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--vela-glass-border-bright)',
  background: 'var(--vela-glass-bg)', color: 'var(--vela-text)',
  borderRadius: 8, fontSize: 14, marginBottom: 14, outline: 'none', fontFamily: 'inherit',
};
const buttonStyle = {
  width: '100%', position: 'relative', overflow: 'hidden', border: 'none',
  borderRadius: 100, padding: '13px', fontSize: 15, fontWeight: 600,
  cursor: 'pointer', marginTop: 8, color: 'var(--primary-contrast)',
  backgroundImage: 'var(--vela-opal-grad)',
};
const buttonTextStyle = { position: 'relative', zIndex: 1 };
const switchModeStyle = { textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--vela-text-mid)' };
const switchModeLinkStyle = { color: 'var(--vela-text)', cursor: 'pointer', fontWeight: 500 };
